// Curated list of branded fuel networks an FBO can be affiliated with. FBOs pick
// their brand from this list (admin + owner portal); the FBO detail page renders
// a branded provider widget below the fuel price history based on the match.

export interface FuelBrandCard {
  name: string;
  benefit: string;
  /** Accent color for the faux card graphic. */
  accent: string;
  accentDark: string;
}

export interface FuelBrand {
  /** Canonical name — this exact string is stored on the FBO and shown in the dropdown. */
  id: string;
  /** Short wordmark used in the widget header. */
  short: string;
  /** Primary brand color (hex). */
  color: string;
  /** Gradient end / darker shade (hex). */
  colorDark: string;
  /** Public marketing site. */
  website: string;
  /** One-line description shown in the widget body. */
  tagline: string;
  /** Optional payment/loyalty cards featured in the widget. */
  cards?: FuelBrandCard[];
}

export const FUEL_BRANDS: readonly FuelBrand[] = [
  {
    id: "Avfuel",
    short: "Avfuel",
    color: "#d6181f",
    colorDark: "#8e0b13",
    website: "https://www.avfuel.com/",
    tagline:
      "Independent contract fuel network with the AVTRIP rewards program and the Avfuel Contract Fuel Card.",
  },
  {
    id: "World Fuel Services",
    short: "World Fuel",
    color: "#0033a0",
    colorDark: "#001f63",
    website: "https://www.wfscorp.com/",
    tagline:
      "Global fuel supply, logistics, and trip support across thousands of locations worldwide.",
  },
  {
    id: "Titan Aviation Fuels",
    short: "Titan",
    color: "#112e51",
    colorDark: "#07203f",
    website: "https://www.titanfuels.aero/",
    tagline:
      "Contract fuel and flexible payment solutions backed by Titan's nationwide branded network.",
    cards: [
      {
        name: "Contract Fuel Card",
        benefit:
          "Lock in contract fuel pricing at thousands of locations nationwide.",
        accent: "#112e51",
        accentDark: "#07203f",
      },
      {
        name: "Multi Service Aviation Card",
        benefit: "One card for fuel, services, and trip expenses worldwide.",
        accent: "#6b7280",
        accentDark: "#374151",
      },
      {
        name: "Rewards Card",
        benefit: "Earn rewards on every gallon of fuel you purchase.",
        accent: "#c8102e",
        accentDark: "#8e0b20",
      },
    ],
  },
  {
    id: "EPIC Fuels",
    short: "EPIC",
    color: "#00843d",
    colorDark: "#005a29",
    website: "https://www.epicfuels.com/",
    tagline:
      "A nationwide branded fuel network with the EPIC Card and a flexible contract fuel program.",
  },
  {
    id: "Phillips 66 Aviation",
    short: "Phillips 66",
    color: "#ee2e24",
    colorDark: "#a01a14",
    website: "https://www.phillips66aviation.com/",
    tagline:
      "The WingPoints rewards program and Phillips 66 Aviation contract fuel at branded locations nationwide.",
  },
];

/** Canonical brand names for dropdowns. */
export const FUEL_BRAND_NAMES: readonly string[] = FUEL_BRANDS.map((b) => b.id);

// Legacy / shorthand values seen in free-text data, mapped to canonical ids.
const FUEL_BRAND_ALIASES: Record<string, string> = {
  avfuel: "Avfuel",
  "world fuel": "World Fuel Services",
  "world fuel services": "World Fuel Services",
  wfs: "World Fuel Services",
  titan: "Titan Aviation Fuels",
  "titan aviation": "Titan Aviation Fuels",
  "titan aviation fuels": "Titan Aviation Fuels",
  epic: "EPIC Fuels",
  "epic fuels": "EPIC Fuels",
  "phillips 66": "Phillips 66 Aviation",
  "phillips 66 aviation": "Phillips 66 Aviation",
  p66: "Phillips 66 Aviation",
};

// Strip punctuation, lowercase, and collapse whitespace so variants like
// "Phillips66", "W.F.S.", or "World Fuel Services, Inc." normalize consistently.
function normalizeBrand(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");
}

const NORMALIZED_ALIASES: Record<string, string> = Object.fromEntries(
  Object.entries(FUEL_BRAND_ALIASES).map(([alias, id]) => [
    normalizeBrand(alias),
    id,
  ]),
);

/** Resolve a stored (possibly free-text) fuel brand value to a known brand. */
export function matchFuelBrand(
  value: string | null | undefined,
): FuelBrand | undefined {
  const v = value?.trim().toLowerCase();
  if (!v) return undefined;
  const exact =
    FUEL_BRAND_ALIASES[v] &&
    FUEL_BRANDS.find((b) => b.id === FUEL_BRAND_ALIASES[v]);
  if (exact) return exact;
  const byId = FUEL_BRANDS.find((b) => b.id.toLowerCase() === v);
  if (byId) return byId;
  // Fall back to punctuation-insensitive matching for legacy free-text values.
  const norm = normalizeBrand(v);
  if (!norm) return undefined;
  const canonical = NORMALIZED_ALIASES[norm];
  if (canonical) return FUEL_BRANDS.find((b) => b.id === canonical);
  return FUEL_BRANDS.find((b) => normalizeBrand(b.id) === norm);
}
